// src/components/modals/upsert-event-v2/UpsertEventPricingStep.jsx
import { useEffect, useState } from "react";
import { Dropdown, Input } from "@oc-labs/wt-storybook";
import useAPI from "../../../hooks/useAPI";
import useRwas from "../../../hooks/useRwas";
import useCompany from "../../../hooks/useCompany";
import AssetDropup from "./AssetDropup";

// consistent name
const currencyCode = (c) =>
  String(c?.slug ?? c?.symbol ?? c?.id ?? c?.name ?? "").trim().toUpperCase();

// convert to number
const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// convert a ""
const displayIfZeroEmpty = (v) => (v === 0 ? "" : (v ?? ""));

export default function UpsertEventPricingStep({ event, setEvent }) {
  const { activeCompany: company } = useCompany();
  const api = useAPI();
  const { getAllRWAs } = useRwas();

  // Currency
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [currenciesError, setCurrenciesError] = useState("");

  // Assets
  const [assetOptions, setAssetOptions] = useState([{ label: "None", value: "" }]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState("");

  // discountsDisabled
  const priceNum = toNumber(event?.price);
  const discountsDisabled = !(priceNum > 0);

  // Load currency
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingCurrencies(true);
        setCurrenciesError("");
        const data = await api.getCurrencies();
        if (cancelled) return;

        const arr = Array.isArray(data) ? data : [];
        const mapped = arr.map((c) => {
          const code = currencyCode(c);
          const name = c?.name ?? code;
          const sym = c?.symbol ? ` (${String(c.symbol).toUpperCase()})` : "";
          return { label: `${name}${sym}`, value: code };
        });

        setCurrencyOptions(mapped);
      } catch {
        if (!cancelled) {
          setCurrenciesError("Could not load currencies");
          setCurrencyOptions([]);
        }
      } finally {
        if (!cancelled) setLoadingCurrencies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load assets 
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!company?.id) {
        setAssetOptions([{ label: "None", value: "" }]);
        return;
      }

      try {
        setLoadingAssets(true);
        setAssetsError("");

        const allRwas = await getAllRWAs(company.id);
        if (cancelled) return;

        const sorted = [...(Array.isArray(allRwas) ? allRwas : [])].sort(
          (a, b) =>
            new Date(b?.updatedAt || b?.modifiedAt || b?.createdAt || 0) -
            new Date(a?.updatedAt || a?.modifiedAt || a?.createdAt || 0)
        );

        const options = [
          { label: "None", value: "" },
          ...sorted
            .filter((r) => r && (r.id != null || r.rwaId != null))
            .map((r) => {
              const id = String(r?.id ?? r?.rwaId ?? "");
              const name = String(r?.name ?? r?.title ?? id).trim();
              const type = String(r?.type ?? r?.category ?? "").trim();
              return {
                label: `${name}${type ? ` — ${type}` : ""}`,
                value: id,
              };
            }),
        ];

        setAssetOptions(options);
      } catch (error) {
        if (!cancelled) {
          setAssetsError("Could not load assets");
          setAssetOptions([{ label: "None", value: "" }]);
        }
      } finally {
        if (!cancelled) setLoadingAssets(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [company?.id]);

  return (
    <>
      {/* Price + Currency */}
      <div className="grid grid-cols-2 mt-2 mb-6 gap-4">
        <Input
          type="number"
          value={displayIfZeroEmpty(event.price)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              setEvent((p) => ({ ...p, price: "" }));
              return;
            }
            const n = Number(raw);
            if (Number.isFinite(n) && n >= 0) {
              setEvent((p) => ({ ...p, price: raw })); 
            }
          }}
          label="Price"
          placeholder="Enter price"
          className="w-full"
          min={0}
          step="0.01"
        />
        <Dropdown
          value={event.currency}
          onChange={(v) => setEvent((p) => ({ ...p, currency: v }))}
          label="Currency"
          placeholder={loadingCurrencies ? "Loading..." : "Select currency"}
          options={currencyOptions}
          disabled={loadingCurrencies || !!currenciesError}
        />
      </div>

      {/* Discount price + % */}
      <div className="grid grid-cols-2 mb-6 gap-4">
        <Input
          type="number"
          value={displayIfZeroEmpty(event.discountPrice)}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              setEvent((p) => ({ ...p, discountPrice: "" }));
              return;
            }
            const n = Number(raw);
            if (Number.isFinite(n) && n >= 0 && n <= priceNum) {
              setEvent((p) => ({ ...p, discountPrice: raw }));
            }
          }}
          label="Discount Price"
          placeholder={"Enter discount price"}
          className="w-full"
          min={0}
          step="0.01"
          disabled={discountsDisabled}
        />
        <Input
          type="number"
          value={displayIfZeroEmpty(event.discountPercentage)}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "") {
              setEvent((p) => ({ ...p, discountPercentage: "" }));
              return;
            }
            const n = Number(val);
            if (Number.isFinite(n) && n >= 0 && n <= 100) {
              setEvent((p) => ({ ...p, discountPercentage: val }));
            }
          }}
          label="Discount %"
          placeholder={"0 - 100"}
          className="w-full"
          min={0}
          max={100}
          step="1"
          disabled={discountsDisabled}
        />
      </div>

     {/* Assets required to attend */}
      <div className="mb-6">
        <label className="block mb-1 font-medium">Asset required to attend</label>

        {/* Badges de los seleccionados */}
        <div className="flex flex-wrap gap-2 mb-2">
          {Array.isArray(event.contractRequiredToAttend) &&
            event.contractRequiredToAttend.map((id) => {
              const asset = assetOptions.find((o) => o.value === id);
              if (!asset) return null;
              return (
                <span
                  key={id}
                  className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full flex items-center gap-1"
                >
                  {asset.label}
                  <button
                    type="button"
                    onClick={() =>
                      setEvent((p) => ({
                        ...p,
                        contractRequiredToAttend: p.contractRequiredToAttend.filter((x) => x !== id),
                      }))
                    }
                  >
                    ×
                  </button>
                </span>
              );
            })}
        </div>

        <AssetDropup
          options={assetOptions}
          value={""}
          onChange={(val) => {
            if (val && !event.contractRequiredToAttend.includes(val)) {
              setEvent((p) => ({
                ...p,
                contractRequiredToAttend: [...p.contractRequiredToAttend, val],
              }));
            }
          }}
          placeholder={loadingAssets ? "Loading..." : "Select asset"}
          disabled={loadingAssets || !!assetsError}
        />
      </div>


      {/* Assets required to get discount */}
      <div className="mb-6">
        <label className="block mb-1 font-medium">Asset required to get discount</label>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-2">
          {Array.isArray(event.contractRequiredToDiscount) &&
            event.contractRequiredToDiscount.map((id) => {
              const asset = assetOptions.find((o) => o.value === id);
              if (!asset) return null;
              return (
                <span
                  key={id}
                  className="bg-green-200 text-green-800 px-2 py-1 rounded-full flex items-center gap-1"
                >
                  {asset.label}
                  <button
                    type="button"
                    onClick={() =>
                      setEvent((p) => ({
                        ...p,
                        contractRequiredToDiscount: p.contractRequiredToDiscount.filter((x) => x !== id),
                      }))
                    }
                  >
                    ×
                  </button>
                </span>
              );
            })}
        </div>

        <AssetDropup
          options={assetOptions}
          value={""}
          onChange={(val) => {
            if (val && !event.contractRequiredToDiscount.includes(val)) {
              setEvent((p) => ({
                ...p,
                contractRequiredToDiscount: [...p.contractRequiredToDiscount, val],
              }));
            }
          }}
          placeholder={loadingAssets ? "Loading..." : "Select asset"}
          disabled={loadingAssets || !!assetsError}
        />

</div>

      {currenciesError && <div className="text-xs text-red-500 mt-2">{currenciesError}</div>}
      {assetsError && <div className="text-xs text-red-500 mt-2">{assetsError}</div>}
    </>
  );
}